import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Size of a review panel in Alert Review V2, in pixels.
 *
 * The camera and the alert frame size INDEPENDENTLY: an operator reading a
 * detection wants the frame big, and one watching the approach wants the video
 * big, and those are different moments in the same review.
 */
export interface AlertReviewPanelSize {
  width: number;
  height: number;
}

export const ALERT_REVIEW_PANEL_DEFAULT: AlertReviewPanelSize = { width: 479, height: 440 };
export const ALERT_REVIEW_PANEL_MIN: AlertReviewPanelSize = { width: 320, height: 240 };

/**
 * Share of the viewport kept clear between the panels so the map — and the
 * alert's own GPS marker on it — stays readable. The panels are what give way
 * when the window is small; the corridor is why the mode sits on the map at all
 * rather than replacing it.
 */
export const ALERT_REVIEW_CORRIDOR_RATIO = 0.2;
export const ALERT_REVIEW_CORRIDOR_MIN_PX = 300;

export type ReviewPanel = "alert" | "video";

interface AlertReviewLayoutState {
  alertSize: AlertReviewPanelSize;
  videoSize: AlertReviewPanelSize;
  setPanelSize: (panel: ReviewPanel, size: AlertReviewPanelSize) => void;
  resetPanelSizes: () => void;
}

/**
 * Persisted so an operator who sized these once does not resize them every
 * shift. Layout only — nothing about WHICH alert is open belongs here.
 */
export const useAlertReviewLayoutStore = create<AlertReviewLayoutState>()(
  persist(
    (set) => ({
      alertSize: ALERT_REVIEW_PANEL_DEFAULT,
      videoSize: ALERT_REVIEW_PANEL_DEFAULT,
      setPanelSize: (panel, size) =>
        set(panel === "alert" ? { alertSize: size } : { videoSize: size }),
      resetPanelSizes: () =>
        set({ alertSize: ALERT_REVIEW_PANEL_DEFAULT, videoSize: ALERT_REVIEW_PANEL_DEFAULT }),
    }),
    // v2: one shared `panelSize` became two independent ones.
    { name: "alert-review-layout", version: 2 },
  ),
);

/** The corridor this stage must keep clear. */
export const corridorFor = (stageWidth: number): number =>
  Math.max(ALERT_REVIEW_CORRIDOR_MIN_PX, Math.round(stageWidth * ALERT_REVIEW_CORRIDOR_RATIO));

/**
 * The largest ONE panel may be, given what the panel opposite it already takes.
 *
 * The two panels and the corridor share the stage, so growing one is paid for by
 * the other's width — not by the corridor, which is the whole reason the review
 * sits on the map.
 */
export const maxPanelWidthFor = (stageWidth: number, otherPanelWidth: number): number =>
  Math.max(
    ALERT_REVIEW_PANEL_MIN.width,
    Math.floor(stageWidth - corridorFor(stageWidth) - otherPanelWidth),
  );

/** Clamp a requested size into what the stage can actually hold. */
export const clampPanelSize = (
  size: AlertReviewPanelSize,
  stage: { width: number; height: number },
  otherPanelWidth: number = ALERT_REVIEW_PANEL_DEFAULT.width,
): AlertReviewPanelSize => ({
  width: Math.round(
    Math.min(
      Math.max(size.width, ALERT_REVIEW_PANEL_MIN.width),
      maxPanelWidthFor(stage.width, otherPanelWidth),
    ),
  ),
  height: Math.round(
    Math.min(
      Math.max(size.height, ALERT_REVIEW_PANEL_MIN.height),
      Math.max(ALERT_REVIEW_PANEL_MIN.height, stage.height),
    ),
  ),
});
